import type { FormIssues } from '@template-ui/helpers/generated-form.helpers';
import type { WorkflowReasonChoice } from './workflow-reason-catalog';

/** Registered presentation requirements only. This contract grants no command authority. */
export interface WorkflowReasonCaptureContract {
  version: 1;
  contractCode: string;
  purpose:
    | 'review'
    | 'timer'
    | 'return'
    | 'waive'
    | 'override'
    | 'cancel'
    | 'reassign';
  mode: 'none' | 'text' | 'code' | 'combined';
  codeField: string | null;
  textField: string | null;
  codeRequired: boolean;
  textRequired: boolean;
  textMaxLength: number | null;
  permittedAdministratorConfiguration: string[];
}
export interface WorkflowReasonCaptureValue {
  code: string;
  text: string;
}
export interface WorkflowReasonCapturePresentation {
  contract: WorkflowReasonCaptureContract;
  choices: readonly WorkflowReasonChoice[];
  codeLabel?: string;
  textLabel?: string;
  unavailable?: string;
}
const purposes = [
  'review',
  'timer',
  'return',
  'waive',
  'override',
  'cancel',
  'reassign',
];
const configurations = [
  'catalog-values',
  'catalog-labels',
  'catalog-enabled',
  'catalog-order',
];
const field = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-zA-Z][a-zA-Z0-9_]{0,119}$/.test(value);
const plain = (value: unknown): value is string =>
  typeof value === 'string' &&
  !!value.trim() &&
  value === value.trim() &&
  value.length <= 200 &&
  ![...value].some(
    (character) =>
      character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
  );

export function isWorkflowReasonCaptureContract(
  value: unknown,
): value is WorkflowReasonCaptureContract {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  if (
    Object.keys(item).some(
      (key) =>
        ![
          'version',
          'contractCode',
          'purpose',
          'mode',
          'codeField',
          'textField',
          'codeRequired',
          'textRequired',
          'textMaxLength',
          'permittedAdministratorConfiguration',
        ].includes(key),
    ) ||
    item['version'] !== 1 ||
    !plain(item['contractCode']) ||
    typeof item['purpose'] !== 'string' ||
    !purposes.includes(item['purpose']) ||
    typeof item['mode'] !== 'string' ||
    !['none', 'text', 'code', 'combined'].includes(item['mode']) ||
    typeof item['codeRequired'] !== 'boolean' ||
    typeof item['textRequired'] !== 'boolean' ||
    !(
      item['textMaxLength'] === null ||
      (Number.isSafeInteger(item['textMaxLength']) &&
        Number(item['textMaxLength']) > 0)
    ) ||
    !Array.isArray(item['permittedAdministratorConfiguration']) ||
    item['permittedAdministratorConfiguration'].some(
      (entry) => typeof entry !== 'string' || !configurations.includes(entry),
    ) ||
    new Set(item['permittedAdministratorConfiguration']).size !==
      item['permittedAdministratorConfiguration'].length
  )
    return false;
  const hasCode = ['code', 'combined'].includes(String(item['mode'])),
    hasText = ['text', 'combined'].includes(String(item['mode']));
  return (
    (hasCode
      ? field(item['codeField'])
      : item['codeField'] === null && item['codeRequired'] === false) &&
    (hasText
      ? field(item['textField'])
      : item['textField'] === null &&
        item['textRequired'] === false &&
        item['textMaxLength'] === null) &&
    (!hasCode || !hasText || item['codeField'] !== item['textField']) &&
    (hasCode || item['permittedAdministratorConfiguration'].length === 0)
  );
}

/** No supplied profile may silently degrade a governed code to free text. */
export function reasonCaptureIssues(
  presentation: WorkflowReasonCapturePresentation,
  value: WorkflowReasonCaptureValue,
): FormIssues {
  const { contract, choices } = presentation;
  if (
    !isWorkflowReasonCaptureContract(contract) ||
    presentation.unavailable ||
    !Array.isArray(choices) ||
    choices.length > 500 ||
    choices.some(
      (choice) =>
        !choice ||
        typeof choice !== 'object' ||
        Object.keys(choice).some((key) => !['code', 'label'].includes(key)) ||
        !plain(choice.code) ||
        choice.code.length > 120 ||
        !plain(choice.label),
    ) ||
    new Set(choices.map((choice) => choice.code)).size !== choices.length
  )
    return {
      reason:
        presentation.unavailable ||
        'The registered reason requirements are unavailable. Refresh current state.',
    };
  const issues: FormIssues = {};
  if (contract.codeField) {
    if (contract.codeRequired && !choices.length)
      issues[contract.codeField] =
        'No configured reasons are available. Ask an administrator to review this command’s catalog.';
    else if (contract.codeRequired && !value.code)
      issues[contract.codeField] = 'Choose a reason code.';
    else if (
      value.code &&
      !choices.some((choice) => choice.code === value.code)
    )
      issues[contract.codeField] = 'Choose a currently available reason code.';
  }
  if (contract.textField) {
    if (contract.textRequired && !value.text.trim())
      issues[contract.textField] = 'Enter a nonblank reason.';
    else if (
      contract.textMaxLength !== null &&
      value.text.length > contract.textMaxLength
    )
      issues[contract.textField] =
        `Enter at most ${contract.textMaxLength} characters.`;
  }
  return issues;
}

export function reviewReasonCaptureContract(
  required: boolean,
): WorkflowReasonCaptureContract {
  return {
    version: 1,
    contractCode: 'workflow.review.reason.v1',
    purpose: 'review',
    mode: 'text',
    codeField: null,
    textField: 'notes',
    codeRequired: false,
    textRequired: required,
    textMaxLength: 4000,
    permittedAdministratorConfiguration: [],
  };
}

export function timerReasonCaptureContract(
  catalog: boolean,
  operation: 'pause' | 'resume' | 'reset' = 'pause',
): WorkflowReasonCaptureContract {
  return {
    version: 1,
    contractCode: `workflow.timer.${operation}.reason.v1`,
    purpose: 'timer',
    mode: catalog ? 'code' : 'text',
    codeField: catalog ? 'reason' : null,
    textField: catalog ? null : 'reason',
    codeRequired: catalog,
    textRequired: !catalog,
    textMaxLength: catalog ? null : 500,
    permittedAdministratorConfiguration: catalog ? [...configurations] : [],
  };
}
